import { useEffect, useState } from 'react';

// Install-to-home-screen plumbing, shared by the banner and the Settings row.
//
// Chrome/Edge fire `beforeinstallprompt` once, early, and only if the PWA is
// installable. Miss it and there is no way to ask for it again, so the listener
// is registered at module load rather than inside a component, and the event is
// held here for whichever component mounts later.
//
// iOS Safari never fires it and offers no install API at all, so there the only
// route is the Share sheet and all we can do is say so.

const DISMISS_KEY = 'install_prompt_dismissed';
const PRACTISED_KEY = 'has_practised';

let deferredEvent = null;
let installed = false;
const subscribers = new Set();

const notify = () => subscribers.forEach((fn) => fn());

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => {
        // Chrome's own mini-infobar is suppressed by this, which is the point:
        // we show the prompt at a moment the user has reason to say yes.
        e.preventDefault();
        deferredEvent = e;
        notify();
    });
    window.addEventListener('appinstalled', () => {
        deferredEvent = null;
        installed = true;
        notify();
    });
}

export const isStandalone = () =>
    typeof window !== 'undefined' &&
    (window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches);

export const isIosSafari = () => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent;
    // iPadOS 13+ reports itself as a Mac, so it's identified by touch support.
    const ios = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
    if (!ios) return false;
    // Chrome/Firefox/Edge on iOS wrap WebKit but their Share menus differ, and
    // Add to Home Screen isn't reliably there, so the instructions wouldn't fit.
    return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
};

// Set when someone finishes a practice session. The prompt waits for it: asking
// on first load, before they know what Mimique is, is what makes install
// prompts annoying and ignored.
export const markPractised = () => {
    try { localStorage.setItem(PRACTISED_KEY, '1'); } catch { /* private mode */ }
};
export const hasPractised = () => {
    try { return localStorage.getItem(PRACTISED_KEY) === '1'; } catch { return false; }
};

export const isPromptDismissed = () => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
};
export const dismissPrompt = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
};

export const useInstallPrompt = () => {
    const [, forceRender] = useState(0);

    useEffect(() => {
        const fn = () => forceRender((n) => n + 1);
        subscribers.add(fn);
        return () => subscribers.delete(fn);
    }, []);

    // Fires Chrome's native install dialog. Resolves true if they accepted.
    const promptInstall = async () => {
        if (!deferredEvent) return false;
        const event = deferredEvent;
        // Single-use: Chrome rejects a second prompt() on the same event.
        deferredEvent = null;
        notify();
        event.prompt();
        const { outcome } = await event.userChoice;
        return outcome === 'accepted';
    };

    return {
        canInstall: !!deferredEvent,       // Chrome/Edge, installable, not yet installed
        needsIosInstructions: isIosSafari() && !isStandalone(),
        installed: installed || isStandalone(),
        promptInstall,
    };
};
